import type { AnyArray } from "ts-essentials";

import { devWarn } from "@/util/dev.mjs";
import symbolName from "@/util/symbol-name.mjs";

export const forceOverwriteIterable: unique symbol = symbolName(
  "force-overwrite-iterable",
);

type RefObjects = { [key: string]: object };
type RefIterable<V = unknown> = AnyArray<V> | Set<V>;

type PartialRefs<Refs extends RefObjects> = {
  // Iterate over each ref
  [K in keyof Refs]?: {
    // For each key in the ref:
    //   Take each value and make it optional.
    [P in keyof Refs[K]]?: Refs[K][P];
  };
};

type IterableRefs<Refs extends RefObjects> = {
  // Iterate over each ref
  [K in keyof Refs]?: {
    // For each key in the ref:
    //   Check if the value is an array or set and set the prop to an array
    //   whos items are the values of the array or set. Otherwise, set the
    //   prop to never ince it is not an array or set.
    [P in keyof Refs[K]]?: Refs[K][P] extends RefIterable<infer V>
      ? V[]
      : never;
  };
};

class OriginalRefs<Refs extends RefObjects> {
  #refs: Refs;

  // Keeps track of all original values from the ref
  #originalValues: PartialRefs<Refs> = {};

  // Keeps track of the props that were updated
  #updatedValues: PartialRefs<Refs> = {};

  // Keeps track of the arrays items that were appended
  #appendedValues: IterableRefs<Refs> = {};

  constructor(refs: Refs) {
    this.#refs = refs;
  }

  getOriginal<K extends keyof Refs>(key: K): Refs[K] {
    return new Proxy(this.#originalValues[key] ?? {}, {
      get: (target, prop) => {
        // Grabs the original value from the ref (if it was changed), or from the
        // ref directly if it wasn't
        return target[prop] ?? this.#refs[key][prop];
      },

      set() {
        throw new TypeError(`Changing refs is not permitted outside of set().`);
      },
    }) as Refs[K];
  }

  set(valueRefs: PartialRefs<Refs>) {
    for (const [refName, valueObj] of Object.entries(valueRefs) as [
      keyof Refs,
      object,
    ][]) {
      if (!(refName in this.#refs)) {
        devWarn(`Unknown ref "${refName.toString()}" was passed to set().`);

        continue;
      }

      if (!(refName in this.#originalValues)) {
        this.#originalValues[refName] = {};
      }

      if (!(refName in this.#updatedValues)) {
        this.#updatedValues[refName] = {};
      }

      for (const [key, value] of Object.entries(valueObj)) {
        const currentValue = this.#refs[refName][key];

        if (Array.isArray(currentValue)) {
          if (!value[forceOverwriteIterable]) {
            devWarn(
              `Ref "${refName.toString()}.${key}" was replaced, but it is an array. This may overwrite changes made by other features. If this was intentional, please set [forceOverwriteIterable].`,
            );
          } else {
            delete value[forceOverwriteIterable];
          }
        }

        if (!(key in this.#originalValues[refName])) {
          // Keep track of the original value from the ref
          this.#originalValues[refName][key] = currentValue;
        }

        if (!(key in this.#updatedValues[refName])) {
          this.#updatedValues[refName][key] = currentValue;
        }

        // Update the ref to the new value
        this.#refs[refName][key] = value;
      }
    }
  }

  append(arrayRefs: IterableRefs<Refs>) {
    for (const [refName, appendObj] of Object.entries(arrayRefs) as [
      keyof Refs,
      object,
    ][]) {
      if (!(refName in this.#refs)) {
        devWarn(`Unknown ref "${refName.toString()}" was passed to append().`);

        continue;
      }

      if (!(refName in this.#originalValues)) {
        this.#originalValues[refName] = {};
      }

      if (!(refName in this.#appendedValues)) {
        this.#appendedValues[refName] = {};
      }

      for (const [key, items] of Object.entries(appendObj) as [
        PrimitiveKey,
        RefIterable,
      ][]) {
        const currentValue = this.#refs[refName][key];

        if (Array.isArray(currentValue)) {
          if (!Array.isArray(items)) {
            throw TypeError(
              `Attempted to append to "${refName.toString()}.${key.toString()}", but the value was not an array.`,
            );
          }

          if (!(key in this.#originalValues[refName])) {
            this.#originalValues[refName][key] = currentValue;
          }

          if (!(key in this.#appendedValues[refName])) {
            this.#appendedValues[refName][key] = [];
          }

          this.#appendedValues[refName][key].push(...items);

          // Update the ref to the new value. This is a shallow clone to allow access to the original array.
          this.#refs[refName][key] = [...currentValue, ...items];
        } else if ("add" in currentValue && "delete" in currentValue) {
          if (!Array.isArray(items)) {
            throw TypeError(
              `Attempted to append to "${refName.toString()}.${key.toString()}", but the value was not an array.`,
            );
          }

          if (!(key in this.#originalValues[refName])) {
            // Shallow clone the set to allow access to the original set.
            this.#originalValues[refName][key] = new Set(currentValue);
          }

          if (!(key in this.#appendedValues[refName])) {
            this.#appendedValues[refName][key] = [];
          }

          this.#appendedValues[refName][key].push(...items);

          for (const item of items as AnyArray<unknown>) {
            (this.#refs[refName][key] as Set<unknown>).add(item);
          }
        } else {
          throw TypeError(
            `Ref "${refName.toString()}.${key.toString()}" is not an array or set.`,
          );
        }
      }
    }
  }

  restore() {
    // Loop over the original refs and their values
    for (const [refName, updatedObj] of Object.entries(this.#updatedValues) as [
      keyof Refs,
      object,
    ][]) {
      for (const [key, value] of Object.entries(updatedObj) as [
        PrimitiveKey,
        unknown,
      ][]) {
        // Update the ref to its original value
        this.#refs[refName][key] = value;
      }

      delete this.#updatedValues[refName];
      delete this.#originalValues[refName];
    }

    for (const [refName, appendObj] of Object.entries(this.#appendedValues) as [
      keyof Refs,
      object,
    ][]) {
      for (const [key, items] of Object.entries(appendObj) as [
        PrimitiveKey,
        RefIterable,
      ][]) {
        const currentValue = this.#refs[refName][key];

        if (Array.isArray(currentValue)) {
          const updatedValue = [...currentValue];

          for (const item of items) {
            updatedValue.splice(updatedValue.indexOf(item), 1);
          }

          // We have to set this (instead of modifying) so that valtio will pick up the change
          this.#refs[refName][key] = updatedValue;
        } else if ("add" in currentValue && "delete" in currentValue) {
          for (const item of items) {
            currentValue.delete(item);
          }
        }
      }

      delete this.#appendedValues[refName];
      delete this.#originalValues[refName];
    }
  }
}

type IOriginalRefs<Refs extends RefObjects> = OriginalRefs<Refs> & Refs;

export default function mapOriginalRefs<Refs extends RefObjects>(
  refs: Refs,
): IOriginalRefs<Refs> {
  const obj = new OriginalRefs(refs);

  return new Proxy(obj, {
    get: (target, prop) => {
      if (prop in refs) {
        return target.getOriginal(prop as keyof Refs);
      }

      if (target[prop] instanceof Function) {
        return target[prop].bind(obj);
      }

      return target[prop];
    },
    set() {
      throw new TypeError(`Modifying refs is not permitted outside of set().`);
    },
  }) as unknown as IOriginalRefs<Refs>;
}
